/**
 * pdf.js access for the field editor — the ONLY module that touches
 * pdfjs-dist, so component tests can mock this seam instead of the
 * whole library. Renders real pages to canvas (the previous editor
 * showed a single iframe page with a hand-counted page total; counts
 * and geometry here come from the loaded document itself).
 *
 * pdfjs-dist is ESM-only and heavy, so it is imported LAZILY inside
 * loadPdfDocument: merely importing this module (e.g. a component
 * tree that includes the editor) must never pull pdf.js in — it
 * loads only when a document is actually opened.
 */

export interface LoadedPdfPage {
  pageNumber: number; // 1-indexed
  widthPts: number;
  heightPts: number;
}

export interface LoadedPdf {
  numPages: number;
  pages: LoadedPdfPage[];
  /** Render one page into the given canvas, fitted to targetWidthPx CSS pixels. */
  renderPage: (pageNumber: number, canvas: HTMLCanvasElement, targetWidthPx: number) => Promise<void>;
  destroy: () => void;
}

function dataUrlToBytes(dataUrl: string): Uint8Array {
  const parts = dataUrl.split(',');
  const bstr = atob(parts[1] || '');
  const u8arr = new Uint8Array(bstr.length);
  for (let i = 0; i < bstr.length; i++) {
    u8arr[i] = bstr.charCodeAt(i);
  }
  return u8arr;
}

type PdfjsModule = typeof import('pdfjs-dist');

let pdfjsPromise: Promise<PdfjsModule> | null = null;

function loadPdfjs(): Promise<PdfjsModule> {
  if (!pdfjsPromise) {
    pdfjsPromise = (async () => {
      const pdfjsLib = await import('pdfjs-dist');
      const workerUrl = (await import('pdfjs-dist/build/pdf.worker.min.mjs?url')).default;
      if (!pdfjsLib.GlobalWorkerOptions.workerSrc) {
        pdfjsLib.GlobalWorkerOptions.workerSrc = workerUrl;
      }
      return pdfjsLib;
    })();
  }
  return pdfjsPromise;
}

/**
 * Load a PDF from a data URL (stored templates/custom uploads) or an
 * http(s) URL. Throws when the bytes cannot be parsed as a PDF — the
 * caller shows an honest error, never a fake page.
 */
export async function loadPdfDocument(source: string): Promise<LoadedPdf> {
  const pdfjsLib = await loadPdfjs();
  const params = source.startsWith('data:')
    ? { data: dataUrlToBytes(source) }
    : { url: source };
  const doc = await pdfjsLib.getDocument(params as any).promise;

  const pages: LoadedPdfPage[] = [];
  for (let n = 1; n <= doc.numPages; n++) {
    const page = await doc.getPage(n);
    const viewport = page.getViewport({ scale: 1 });
    pages.push({ pageNumber: n, widthPts: viewport.width, heightPts: viewport.height });
    page.cleanup();
  }

  return {
    numPages: doc.numPages,
    pages,
    async renderPage(pageNumber: number, canvas: HTMLCanvasElement, targetWidthPx: number) {
      const page = await doc.getPage(pageNumber);
      const base = page.getViewport({ scale: 1 });
      const cssScale = targetWidthPx / base.width;
      const dpr = Math.min(typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1, 2);
      const viewport = page.getViewport({ scale: cssScale * dpr });
      canvas.width = Math.max(1, Math.floor(viewport.width));
      canvas.height = Math.max(1, Math.floor(viewport.height));
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('Canvas 2D context unavailable');
      await page.render({ canvas, canvasContext: ctx, viewport }).promise;
    },
    destroy() {
      doc.destroy().catch(() => {});
    },
  };
}
